import axios from 'axios';
import { getBackendUrl } from '../config/apiConfig';
import { getCorrelationIdFromResponse } from './telemetryService';
import authService from './authService';

const backendUrl = getBackendUrl();
const FORM_PDF_URL_PATTERN = /\/api\/v1\/admin\/forms\/([^/]+)\/([^/]+)\/pdf$/;
const BASE_PATH = '/api/v1/admin/forms';
const DOWNLOAD_URL_PATTERNS = {
  pdf: /\/api\/v1\/admin\/forms\/([^/]+)\/([^/]+)\/pdf$/,
  uploadsZip: /\/api\/v1\/admin\/forms\/([^/]+)\/([^/]+)\/uploads\.zip$/,
  file: /\/api\/v1\/admin\/forms\/([^/]+)\/([^/]+)\/files\/([^/]+)$/, 
};

const logSecureDownloadService = (event, details = {}) => {
  console.info('[secure-download:service]', event, details);
};

const parseDownloadContext = (url) => {
  try {
    const parsedUrl = new URL(url, backendUrl);
    const pathname = parsedUrl.pathname;

    const pdfMatch = pathname.match(DOWNLOAD_URL_PATTERNS.pdf);
    if (pdfMatch) {
      return {
        requestPath: pathname,
        formType: decodeURIComponent(pdfMatch[1]),
        formToken: decodeURIComponent(pdfMatch[2]),
        downloadKind: 'pdf',
        requestedCategory: null,
      };
    }

    const zipMatch = pathname.match(DOWNLOAD_URL_PATTERNS.uploadsZip);
    if (zipMatch) {
      return {
        requestPath: pathname,
        formType: decodeURIComponent(zipMatch[1]),
        formToken: decodeURIComponent(zipMatch[2]),
        downloadKind: 'uploads-zip',
        requestedCategory: null,
      };
    }

    const fileMatch = pathname.match(DOWNLOAD_URL_PATTERNS.file);
    if (fileMatch) {
      return {
        requestPath: pathname,
        formType: decodeURIComponent(fileMatch[1]),
        formToken: decodeURIComponent(fileMatch[2]),
        downloadKind: 'file',
        requestedCategory: decodeURIComponent(fileMatch[3]),
      };
    }

    return { requestPath: pathname };
  } catch {
    return { requestPath: url };
  }
};

export const parseFormPdfUrl = (pdfDownloadUrl) => {
  if (!pdfDownloadUrl) return null;
  const match = pdfDownloadUrl.match(FORM_PDF_URL_PATTERN);
  if (!match) return null;
  return {
    formType: decodeURIComponent(match[1]),
    formToken: decodeURIComponent(match[2]),
  };
};

export const pdfUrl = (formType, formToken) => {
  return `${BASE_PATH}/${encodeURIComponent(formType)}/${encodeURIComponent(
    formToken
  )}/pdf`;
};

export const uploadsZipUrl = (formType, formToken) => {
  return `${BASE_PATH}/${encodeURIComponent(formType)}/${encodeURIComponent(
    formToken
  )}/uploads.zip`;
};

export const fileUrl = (formType, formToken, fileCategory) => {
  return `${BASE_PATH}/${encodeURIComponent(formType)}/${encodeURIComponent(
    formToken
  )}/files/${encodeURIComponent(fileCategory)}`;
};

export const otpRequestUrl = (formType, formToken) => {
  return `${BASE_PATH}/${encodeURIComponent(formType)}/${encodeURIComponent(
    formToken
  )}/otp/request`;
};

export const otpVerifyUrl = (formType, formToken) => {
  return `${BASE_PATH}/${encodeURIComponent(formType)}/${encodeURIComponent(
    formToken
  )}/otp/verify`;
};

export const resendEmailUrl = (formType, formToken) => {
  return `${BASE_PATH}/${encodeURIComponent(formType)}/${encodeURIComponent(
    formToken
  )}/resend-email`;
};

const DOWNLOAD_TIMEOUT_MS = 20000;

export async function downloadBinary(url, fallbackFilename) {
  const requestContext = parseDownloadContext(url);

  logSecureDownloadService('download-request-start', {
    ...requestContext,
    fallbackFilename: fallbackFilename || null,
  });

  // Use fetch() instead of axios for blob downloads. The streaming reader in
  // res.blob() tolerates Content-Length mismatches that cause axios to throw
  // ERR_CONTENT_LENGTH_MISMATCH as a network error even when the response
  // body arrived successfully. We still guard with an AbortController so that
  // a stalled body (e.g. server declared a Content-Length that does not match
  // the streamed bytes) fails fast instead of hanging the UI forever.
  const requestUrl = `${backendUrl}${url}`;
  const token = authService.getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const abortTimer = setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(requestUrl, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
  } catch (error) {
    clearTimeout(abortTimer);
    const aborted = error?.name === 'AbortError';
    logSecureDownloadService('download-request-failure', {
      ...requestContext,
      status: null,
      errorMessage: error?.message || null,
      errorCode: aborted ? 'ETIMEDOUT' : error?.name || null,
      hasResponse: false,
      hasRequest: true,
      aborted,
    });

    const networkError = new Error(
      aborted
        ? 'Download timed out'
        : error?.message || 'Network Error'
    );
    networkError.request = error?.request || {};
    networkError.config = { url: requestUrl, method: 'get' };
    if (aborted) {
      networkError.code = 'ECONNABORTED';
    }
    throw networkError;
  }

  const correlationId =
    response.headers.get('x-correlation-id') ||
    response.headers.get('X-Correlation-Id') ||
    null;

  if (!response.ok) {
    let errorBody = null;
    try {
      errorBody = await response.clone().json();
    } catch {
      // Body was not JSON; that is fine, we just won't surface a body message.
    }

    logSecureDownloadService('download-response-error', {
      ...requestContext,
      status: response.status,
      contentType: response.headers.get('content-type'),
    });

    const httpError = new Error(
      errorBody?.message || `Request failed with status ${response.status}`
    );
    httpError.response = {
      status: response.status,
      headers: {
        'content-type': response.headers.get('content-type'),
        'x-correlation-id': correlationId,
      },
      data: errorBody || {},
    };
    httpError.config = { url: requestUrl, method: 'get' };
    throw httpError;
  }

  logSecureDownloadService('download-response-received', {
    ...requestContext,
    status: response.status,
    contentType: response.headers.get('content-type'),
    contentLength: response.headers.get('content-length'),
  });

  let blob;
  try {
    blob = await response.blob();
  } catch (error) {
    logSecureDownloadService('download-body-failure', {
      ...requestContext,
      status: response.status,
      errorMessage: error?.message || null,
    });

    const bodyError = new Error(
      error?.message || 'Failed to read download body'
    );
    bodyError.request = {};
    bodyError.config = { url: requestUrl, method: 'get' };
    throw bodyError;
  }

  const contentDisposition = response.headers.get('content-disposition') || '';
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch ? filenameMatch[1] : fallbackFilename;

  logSecureDownloadService('download-save-start', {
    ...requestContext,
    filename,
    blobSize: blob.size,
  });

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);

  logSecureDownloadService('download-save-complete', {
    ...requestContext,
    filename,
  });

  return {
    status: response.status,
    headers: {
      'content-type': response.headers.get('content-type'),
      'content-length': response.headers.get('content-length'),
      'x-correlation-id': correlationId,
    },
    data: blob,
  };
}

export async function requestOtp(formType, formToken) {
  const response = await axios.post(
    `${backendUrl}${otpRequestUrl(formType, formToken)}`
  );
  return {
    ...(response.data && typeof response.data === 'object'
      ? response.data
      : { value: response.data }),
    correlationId: getCorrelationIdFromResponse(response),
  };
}

export async function verifyOtp(formType, formToken, code) {
  const response = await axios.post(
    `${backendUrl}${otpVerifyUrl(formType, formToken)}`,
    { code },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return {
    ...(response.data && typeof response.data === 'object'
      ? response.data
      : { value: response.data }),
    correlationId: getCorrelationIdFromResponse(response),
  };
}
